import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge, Card, ErrorState, LoadingState, Tabs } from '@/components/ui';
import { useBranch } from '@/features/branch/branch-context';
import {
  useClinicChats,
  type ChatFilter,
  type ClinicChatListItem,
} from '@/features/clinic-chat/api';
import { PageContainer } from '@/layouts/PageContainer';
import { useClinicChatLive } from '@/features/clinic-chat/live';
import { ChatConversation } from './ChatConversation';
import { chatTime } from './chat-format';
import styles from './PatientChatPage.module.css';

/**
 * RL-1673 (gap #4): "Patient chat" for the nurses: the chats patients start
 * in the app (Help › Chat) at the selected branch. One list with an unread
 * dot, Open / Closed, and the chosen chat beside it (`?chat=`, RL-1674:
 * read, reply, close; live updates).
 */
export function PatientChatPage() {
  const { t } = useTranslation();
  const { selected: branch, canSelectAll } = useBranch();
  const [params, setParams] = useSearchParams();
  const filter: ChatFilter =
    params.get('filter') === 'closed' ? 'closed' : 'open';
  const chosen = params.get('chat');
  // RL-1674: new messages arrive live (no reload).
  useClinicChatLive(!!branch || canSelectAll);
  /** One URL update for every changed key (two in a row would drop one). */
  const set = (changes: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(changes))
          if (value) next.set(key, value);
          else next.delete(key);
        return next;
      },
      { replace: true },
    );
  if (!branch && !canSelectAll)
    return (
      <PageContainer>
        <p className={styles.empty}>{t('patientChat.chooseBranch')}</p>
      </PageContainer>
    );
  return (
    <PageContainer>
      <div className={styles.layout}>
        <Card
          title={t('patientChat.title')}
          actions={
            <Tabs
              label={t('patientChat.filter')}
              value={filter}
              onChange={(v) =>
                set({ filter: v === 'open' ? null : v, chat: null })
              }
              items={[
                { value: 'open', label: t('patientChat.open') },
                { value: 'closed', label: t('patientChat.closed') },
              ]}
            />
          }
        >
          <ChatList
            filter={filter}
            chosen={chosen}
            onChoose={(id) => set({ chat: id })}
          />
        </Card>
        <Card title={t('patientChat.conversation')}>
          {chosen ? (
            <ChatConversation key={chosen} id={chosen} />
          ) : (
            <p className={styles.empty}>{t('patientChat.choose')}</p>
          )}
        </Card>
      </div>
    </PageContainer>
  );
}

function ChatList({
  filter,
  chosen,
  onChoose,
}: {
  filter: ChatFilter;
  chosen: string | null;
  onChoose: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useClinicChats(filter);
  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data?.length)
    return (
      <p className={styles.empty}>
        {filter === 'open'
          ? t('patientChat.noneOpen')
          : t('patientChat.noneClosed')}
      </p>
    );
  return (
    <ul className={styles.list} aria-label={t('patientChat.list')}>
      {data.map((c) => (
        <li key={c.id}>
          <ChatRow
            c={c}
            selected={c.id === chosen}
            onClick={() => onChoose(c.id)}
          />
        </li>
      ))}
    </ul>
  );
}

function ChatRow({
  c,
  selected,
  onClick,
}: {
  c: ClinicChatListItem;
  selected: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const unread = (c.unreadCount ?? 0) > 0;
  const last = c.lastMessage;
  return (
    <button
      type="button"
      className={[
        styles.row,
        selected && styles.selected,
        unread && styles.unread,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-current={selected ? 'true' : undefined}
      aria-label={`${c.patient.fullName}${unread ? `, ${t('patientChat.unread', { count: c.unreadCount })}` : ''}`}
      onClick={onClick}
    >
      <span className={styles.name}>
        {unread && <span className={styles.dot} aria-hidden="true" />}
        {c.patient.fullName}
      </span>
      <span className={styles.time}>{chatTime(c.lastMessageAt)}</span>
      <p className={styles.preview}>
        {last
          ? `${last.authorType === 'staff' ? t('patientChat.you') : ''}${last.text}`
          : t('patientChat.messages', { count: c.messageCount })}
      </p>
      <span className={styles.tags}>
        {c.status === 'open' && (
          <Badge tone="warning">{t('patientChat.waiting')}</Badge>
        )}
        {c.status === 'answered' && (
          <Badge tone="success">{t('patientChat.answered')}</Badge>
        )}
        {c.receivedOutOfHours && c.status === 'open' && (
          <Badge>{t('patientChat.outOfHours')}</Badge>
        )}
        {c.identityPending && (
          <Badge tone="info">{t('patientChat.identityPending')}</Badge>
        )}
        {c.closeReason === 'account_deleted' && (
          <Badge>{t('patientChat.accountDeleted')}</Badge>
        )}
      </span>
    </button>
  );
}
